import { useCallback, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../hook";
import { GalleryFlow } from "../types/types";
export default function useMediaFiles(source, { pageSize } = {}) {
    const dispatch = useAppDispatch();
    const { selectState, fetchFiles } = source;
    const { files, filters, sort, nextCursor, hasLoadedFirstPage, resetCount, loading, loadingType, error } = useAppSelector(selectState);
    // The end-of-list callback can fire several times in one frame, before React commits the loading
    // state, so the "already fetching" and "reached the end" guards live in refs, never in state.
    const refIsFetching = useRef(false);
    const refNextCursor = useRef(nextCursor);
    const refRequestSeq = useRef(0);
    const fetchPage = useCallback(async (cursor) => {
        refRequestSeq.current += 1;
        const sequence = refRequestSeq.current;
        refIsFetching.current = true;
        // A first page starts a new listing, so the old cursor must not be reused while it is in flight or if it fails.
        if (cursor === undefined) {
            refNextCursor.current = null;
        }
        const action = await dispatch(fetchFiles({ ...filters, sort, limit: pageSize, cursor }));
        // A newer request owns the outcome, so a superseded one must leave the guards alone.
        if (sequence !== refRequestSeq.current) {
            return;
        }
        // A failure keeps the previous cursor so the same page can be retried.
        if (fetchFiles.fulfilled.match(action)) {
            refNextCursor.current = action.payload.next_cursor;
        }
        refIsFetching.current = false;
    }, [dispatch, fetchFiles, filters, sort, pageSize]);
    // The slice keeps the current files until the new first page lands, so a refresh never blanks the grid.
    const refresh = useCallback(() => {
        void fetchPage();
    }, [fetchPage]);
    const loadNextPage = useCallback(() => {
        if (refIsFetching.current || refNextCursor.current === null) {
            return;
        }
        void fetchPage(refNextCursor.current);
    }, [fetchPage]);
    const retry = useCallback(() => {
        if (error?.errorType === GalleryFlow.GET_MORE_MEDIA_FILES) {
            loadNextPage();
        }
        else {
            refresh();
        }
    }, [error, loadNextPage, refresh]);
    // Filters and sort live in the slice; whenever they change the list starts over from the first page.
    useEffect(() => {
        refresh();
    }, [refresh]);
    // A reset drops whatever was in flight and empties the slice without touching filters or sort, so it needs
    // its own trigger to reload page one. The counter catches a reset in any state, including mid-fetch.
    const refSeenResetCount = useRef(resetCount);
    useEffect(() => {
        if (refSeenResetCount.current === resetCount) {
            return;
        }
        refSeenResetCount.current = resetCount;
        refresh();
    }, [resetCount, refresh]);
    const isLoadingFirstFlow = loading && loadingType === GalleryFlow.GET_MEDIA_FILES;
    return {
        files,
        isLoadingFirstPage: isLoadingFirstFlow && !hasLoadedFirstPage,
        isRefreshing: isLoadingFirstFlow && hasLoadedFirstPage,
        isLoadingMore: loading && loadingType === GalleryFlow.GET_MORE_MEDIA_FILES,
        hasMore: nextCursor !== null,
        hasLoadedFirstPage,
        // Stays true while an empty gallery refreshes, so the view keeps its card; an error takes its place.
        isEmpty: hasLoadedFirstPage && files.length === 0 && error === null,
        error,
        loadNextPage,
        refresh,
        retry,
    };
}
